// Client-side error logging utility
// Follows the same fire-and-forget pattern as trackStats.ts

import {
  isDomManipulationError,
  isHydrationMismatchError,
  isLegacyBrowserSyntaxError,
  isWalletExtensionError,
  isExtensionScriptError,
  isChunkLoadError,
  isExtensionNoiseError,
  isTransientNetworkError,
  isBrowserDomNoiseError,
  isInjectedInlineHandlerError,
  isForeignStackOverflow,
  isProxiedSessionError,
  isExtensionRpcTimeout,
  isAutomationBridgeError,
} from './errorFilters';

const recentErrors = new Set<string>();
const DEDUP_WINDOW_MS = 10_000;
const MAX_MESSAGE_LEN = 500;
const MAX_STACK_LEN = 2000;

// Burst cap: one bad session must not flood the board. The dedup below keys on the message, so N
// DISTINCT messages all get through — production #423–#443 was 21 in 0.5 s from one extension, under
// the server's 30/min limiter. Keeps the FIRST few of a burst, so a real cascade is still seen.
// A crash the user SAW (source 'error_boundary') is never capped: that is the one real error that
// must not be lost behind noise, and the fallback renders once, so it cannot loop.
const BURST_WINDOW_MS = 5_000;
const BURST_MAX = 5;
let burst: number[] = [];

// Redact long URL-hash payloads (base64-like fragments) that can leak
// user-generated content and bloat the error_logs table.
function redactHashPayloads(input: string): string {
  return input.replace(/#[A-Za-z0-9+/=_-]{80,}/g, '#[hash redacted]');
}

function sanitize(input: string | undefined, max: number): string | undefined {
  if (!input) return input;
  const redacted = redactHashPayloads(input);
  if (redacted.length <= max) return redacted;
  return redacted.slice(0, max) + '…[truncated]';
}

export async function logError(
  error: unknown,
  source: 'error_boundary' | 'window_error' | 'unhandled_rejection' | 'manual' = 'manual'
): Promise<void> {
  try {
    const message = error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'Unknown error';

    const stack = error instanceof Error ? error.stack : undefined;
    const url = typeof window !== 'undefined' ? window.location.pathname : '/unknown';

    // Never report from a local dev server. The dev server runs against the SAME
    // Supabase project as production, so every error thrown while developing —
    // including ones deliberately triggered while testing — landed in the real
    // error_logs table and had to be triaged as if a user had hit it.
    //
    // Confirmed, not hypothetical: open row #390 ("dyeIds.map is not a function",
    // beacon-range-calculator) came from an agent browser on http://localhost:4321 during
    // development on 2026-07-20 and sat in the open queue for nine days looking
    // like a production bug.
    //
    // Console still gets it, so nothing is lost while developing — only the POST
    // is suppressed.
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host.endsWith('.local')) {
        console.error('[logError] suppressed on local dev host:', message, error);
        return;
      }
    }

    // Skip non-actionable errors (browser extensions, network, cross-origin,
    // legacy-browser parse errors that already trigger the layout.tsx banner)
    if (
      isDomManipulationError(message) ||
      isLegacyBrowserSyntaxError(error) ||
      isWalletExtensionError(error) ||
      isExtensionScriptError(error) ||
      isChunkLoadError(error) ||
      isExtensionNoiseError(error) ||
      isTransientNetworkError(error) ||
      isBrowserDomNoiseError(error) ||
      isInjectedInlineHandlerError(error) ||
      isForeignStackOverflow(error, message) ||
      isProxiedSessionError(error) ||
      isExtensionRpcTimeout(error) ||
      isAutomationBridgeError(error) ||
      message === 'Script error.'
    ) return;

    // Hardened hydration filter: inspects message AND stack so the filter still
    // catches when error.message is wrapped/empty.
    if (isHydrationMismatchError(error)) return;

    const cleanMessage = sanitize(message, MAX_MESSAGE_LEN) ?? message.slice(0, MAX_MESSAGE_LEN);
    const cleanStack = sanitize(stack, MAX_STACK_LEN);

    // Client-side dedup to prevent spam from render loops. Fingerprint on the
    // sanitized message so we don't hold huge strings in memory for the TTL.
    const fp = `${cleanMessage}|${url}`;
    if (recentErrors.has(fp)) return;

    if (source !== 'error_boundary') {
      const now = Date.now();
      burst = burst.filter((t) => now - t < BURST_WINDOW_MS);
      if (burst.length >= BURST_MAX) return;
      burst.push(now);
    }

    recentErrors.add(fp);
    setTimeout(() => recentErrors.delete(fp), DEDUP_WINDOW_MS);

    await fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: cleanMessage,
        stack: cleanStack,
        url,
        source,
      }),
    });
  } catch {
    // Silently fail — error logging must never cause errors
  }
}
